<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Name Input -->
              <base-input
                col="5"
                type="text"
                :placeholder="$t('PLACEHOLDERS.name')"
                v-model.trim="filterOptions.name"
              />
              <!-- End:: Name Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="5"
                :optionsList="activeStatuses"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.is_active"
              />
              <!-- End:: Status Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.packages") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div
          class="title_route_wrapper"
          v-if="$can('packages create', 'packages')"
        >
          <router-link to="/packages/create">
            {{ $t("TITLES.addPackage") }}
          </router-link>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->
      <div class="d-flex justify-content-between">
        <v-col
          cols="auto"
          md="auto"
          sm="auto"
          class="d-flex justify-center my-4 gap-2"
        >
          <v-btn
            v-if="selected.length"
            @click="openDeleteDialogForSelected"
            size="x-large"
            color="error"
          >
            {{ $t("PLACEHOLDERS.delete_selected") }}
          </v-btn>
        </v-col>
        <base-input
          :label="$t('TITLES.item_per_page')"
          :placeholder="$t('TITLES.item_per_page')"
          col="5"
          type="number"
          class="my-0 base-input py-0"
          v-model="paginations.items_per_page"
        />
      </div>

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        v-model="selected"
        show-select
        item-class="ltr"
        :items-per-page="
          paginations.items_per_page === ''
            ? 10
            : Number(paginations.items_per_page)
        "
        hide-default-footer
      >
        <template v-slot:header.data-table-select>
          <v-checkbox
            :disabled="tableRows.every((row) => row.can_remove === false)"
            v-model="myHeaderCheck"
            @click.stop="handleHeaderClick"
          />
        </template>

        <template v-slot:item.data-table-select="{ isSelected, select, item }">
          <v-checkbox
            :input-value="isSelected"
            @change="select($event)"
            color="primary"
            :disabled="!item.can_remove"
            hide-details
            dense
          />
        </template>
        <template v-slot:[`item.id`]="{ index }">
          {{
            (paginations.current_page - 1) * paginations.items_per_page +
            index +
            1
          }}
        </template>
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <!-- Start:: Activation -->
        <template v-slot:[`item.is_active`]="{ item }">
          <!-- v-if="permissions.activate" -->
          <div
            class="activation"
            dir="ltr"
            style="z-index: 1"
            v-if="$can('packages activate', 'packages')"
          >
            <v-switch
              class="mt-2"
              color="success"
              v-model="item.is_active"
              hide-details
              @change="changeActivationStatus(item)"
            ></v-switch>
          </div>
        </template>
        <!-- End:: Activation -->

        <template v-slot:[`item.created_at`]="{ item }">
          <span>{{ formatDisplayDate(item.created_at) }}</span>
        </template>

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('packages delete', 'packages')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>
            <a-tooltip
              placement="bottom"
              v-if="$can('packages edit', 'packages')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.edit") }}</span>
              </template>

              <button class="btn_edit" @click="editItem(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>
            <a-tooltip
              placement="bottom"
              v-if="$can('packages show', 'packages')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <template v-else>
              <i
                class="fal fa-lock-alt fs-5 blue-grey--text text--darken-1"
              ></i>
            </template>
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("PLACEHOLDERS.subscribers") }}</span>
              </template>
              <button class="btn_show" @click="showSubscribers(item)">
                <i class="fas fa-money-bill-wave"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Image Modal -->
          <image-modal
            v-if="dialogImage"
            :modalIsOpen="dialogImage"
            :modalImage="selectedItemImage"
            @toggleModal="dialogImage = !dialogImage"
          />
          <!-- End:: Image Modal -->

          <!-- Start:: Description Modal -->

          <description-modal
            v-if="dialogDescription"
            :modalIsOpen="dialogDescription"
            :modalDesc="selectedDescriptionTextToShow"
            @toggleModal="dialogDescription = !dialogDescription"
          />
          <!-- End:: Description Modal -->

          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{
                  $t("TITLES.DeleteConfirmingMessage", {
                    name: itemToDelete.name,
                  })
                }}
              </v-card-title>
              <v-card-title v-if="!itemToDelete" class="text-h5 justify-center">
                {{ $t("TITLES.delete_selected") }}
              </v-card-title>
              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  @click="
                    !itemToDelete
                      ? confirmDeleteSelected()
                      : confirmDeleteItem()
                  "
                  >{{ $t("BUTTONS.ok") }}</v-btn
                >

                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- change status -->
          <v-dialog v-model="changeStatus">
            <v-card>
              <v-card-title class="text-h5 justify-center">
                {{ $t("TITLES.chane_selected") }}
              </v-card-title>
              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  @click="confirmChangeSelectedStatus"
                  >{{ $t("BUTTONS.ok") }}</v-btn
                >

                <v-btn class="modal_cancel_btn" @click="changeStatus = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "Allpackages",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    activeStatuses() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.active"),
          value: 1,
        },
        {
          id: 2,
          name: this.$t("STATUS.notActive"),
          value: false,
        },
      ];
    },
  },

  data() {
    return {
      changeStatus: false,
      myHeaderCheck: false,

      selected: [],
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        name: null,
        is_active: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.name"),
          value: "name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.number_of_available_bids"),
          value: "ads_number",
          sortable: false,
          align: "center",
        },

        {
          text: this.$t("PLACEHOLDERS.price"),
          value: "price",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.package_duration"),
          value: "duration",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.package_count_users"),
          value: "subscribers_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "is_active",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogImage: false,
      selectedItemImage: null,
      dialogDescription: false,
      selectedDescriptionTextToShow: "",
      dialogDelete: false,
      itemToDelete: null,
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      regions: [],
      cites: [],
      allSelected: false,
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.myHeaderCheck = false;
      this.selected = [];
      this.setTableRows();
    },
    "paginations.items_per_page"(newVal, oldVal) {
      this.paginations.current_page = 1;
      this.debouncedFetch();
    },
    selected(data) {
      if (data.length == 0) {
        this.myHeaderCheck = false;
      } else {
        this.myHeaderCheck = true;
      }
    },
  },

  methods: {
    formatDisplayDate(value) {
      if (!value) return this.$t("TABLES.noData");
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return value;
      return `${match[3]}/${match[2]}/${match[1]}`;
    },

    handleHeaderClick() {
      if (this.myHeaderCheck) {
        this.selected = this.tableRows.filter((item) => item.can_remove);
      } else {
        this.selected = [];
      }
    },
    async confirmDeleteSelected() {
      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("_method", "DELETE");
      this.selected.map((item, index) => {
        REQUEST_DATA.append(`ids[${index}]`, `${item.id}`);
      });
      try {
        await this.$axios({
          method: "POST",
          url: `packages/remove-many`,
          data: REQUEST_DATA,
        });
        this.dialogDelete = false;

        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.delete_selected_successfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(error.response.data.message);
      }
    },

    debouncedFetch: debounce(function () {
      this.setTableRows();
    }, 500),
    // Start:: Handel Filter
    openDeleteDialogForSelected() {
      this.itemToDelete = null;
      this.dialogDelete = true;
    },

    // Start
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/packages/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.is_active = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/packages/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get packages
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        const params = {
          page: this.paginations.current_page,
          name: this.filterOptions.name,
          status: this.filterOptions.is_active?.value,
          limit:
            this.paginations.items_per_page === ""
              ? 10
              : Number(this.paginations.items_per_page),
        };
        let res = await this.$axios({
          method: "GET",
          url: "packages",
          params: params,
        });

        this.loading = false;
        this.selected = [];
        this.myHeaderCheck = false;

        this.tableRows = res.data.data;
        this.paginations.last_page = res.data.meta.last_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response.data.message);
      }
    },
    // End:: Set Table Rows
    showReplayModal(replay) {
      this.dialogDescription = true;
      this.selectedDescriptionTextToShow = replay;
    },

    // Start:: Control Modals
    showImageModal(image) {
      this.dialogImage = true;
      this.selectedItemImage = image;
    },
    // End:: Control Modals
    // Start:: Change Activation Status
    async changeActivationStatus(item) {
      const REQUEST_DATA = new FormData();
      // Start:: Append Request Data
      // REQUEST_DATA.append("_method", "PUT");
      try {
        await this.$axios({
          method: "POST",
          url: `packages/activate/${item.id}`,
          data: REQUEST_DATA,
        });
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.changeActivation"));
      } catch (error) {
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Change Activation Status

    // ==================== Start:: Crud ====================
    // ===== Start:: End
    editItem(item) {
      this.$router.push({ path: `/packages/edit/${item.id}` });
    },
    showItem(item) {
      this.$router.push({ path: `/packages/show/${item.id}` });
    },
    showSubscribers(item) {
      this.$router.push({ path: `/packages/sub/${item.id}` });
    },
    // ===== End:: End

    // ===== Start:: Delete
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },

    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `packages/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(error.response.data.message);
      }
    },
    // ===== End:: Delete
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>
